import React from "react";

import { formatKstShort } from "../../utils/time";
import { getStrategyMeta } from "../../data/strategyMeta";
import VerdictBadge from "./VerdictBadge";
import CapitalStat from "./CapitalStat";
import {
    readStrategyFields,
    classifyStrategy,
    LOSS_WARN_KRW,
    NewBadge,
} from "./PaperStrategyCard";

/**
 * 표시 이름 끝의 코인 토큰(BNB/ETH/…)을 제거해 패밀리 공통 이름을 만든다.
 * "H318 Basis_delta BNB" → "H318 Basis_delta"
 */
export function stripTrailingCoin(name) {
    if (typeof name !== "string") return name;
    return name.replace(/\s+(BTC|ETH|SOL|XRP|ADA|BNB|DOGE|AAVE|LINK)\s*$/i, "").trim();
}

/**
 * variant_axis 에 따라 패밀리 배지 단위/설명을 고른다.
 *   coin  → "코인"   (같은 전략, 다른 코인)
 *   param → "변형"   (같은 전략, 다른 파라미터: tight/lenient 등)
 *   mixed → "종"     (코인 + 파라미터 혼합)
 */
export function variantLabel(axis) {
    switch (axis) {
        case "param":
            return { unit: "변형", title: "파라미터 변형 패밀리 — 같은 전략, 다른 파라미터" };
        case "mixed":
            return { unit: "종", title: "패밀리 — 같은 전략(H번호)의 코인·파라미터 변형 묶음" };
        case "coin":
        default:
            return { unit: "코인", title: "코인 분리 전략 패밀리 — 같은 전략, 다른 코인" };
    }
}

/**
 * PaperStrategyGroupCard — 코인 분리 전략 "패밀리" 1개를 묶은 집계 카드.
 *
 * groupStrategies() 가 만든 그룹 객체(isGroup:true)를 받는다. 합산된 stats
 * (거래/PnL/WR/PF)를 단일 카드와 동일한 레이아웃으로 보여주고, 카드를 펼치면
 * (드릴다운) 코인별 멤버 내역을 행으로 나열한다. 멤버 행을 클릭하면 해당
 * 코인 전략의 디테일 페이지로 이동한다.
 *
 * 단일 카드(PaperStrategyCard)와 시각적으로 구분되도록 좌측에 패밀리 강조
 * border + "N 코인" 배지를 단다.
 */
export default function PaperStrategyGroupCard({
    group,
    onMemberClick,
    adoptedIds,
    family = null,
    showFamily = false,
    verdict = null,
    newIds = null,
}) {
    const [open, setOpen] = React.useState(false);

    const f = readStrategyFields(group);
    const { id: baseId, pnl, trades, wr, pf, openCount, lastTradeTs, firstTradeTs } = f;
    const members = group.members ?? [];
    const memberCount = group.memberCount ?? members.length;

    // 그룹은 baseId 또는 멤버 코인 중 하나라도 신규면 신규로 본다.
    const newSet = newIds instanceof Set ? newIds : new Set();
    const groupIsNew =
        newSet.has(baseId) ||
        members.some((m) => newSet.has(readStrategyFields(m).id));

    // 멤버는 같은 base 전략의 코인별 변형이므로 첫 멤버 meta 로 패밀리 공통
    // 이름/요약을 만든다 (코인 토큰은 제거해 "H318 Basis_delta" 형태로).
    const firstMeta = members.length
        ? getStrategyMeta(readStrategyFields(members[0]).id)
        : null;
    const displayName = firstMeta?.name ? stripTrailingCoin(firstMeta.name) : baseId;
    const summary = firstMeta?.summary;

    const adoptedSet = adoptedIds instanceof Set ? adoptedIds : new Set();
    const adoptedCount = members.filter((m) => adoptedSet.has(readStrategyFields(m).id))
        .length;

    const pnlClass =
        pnl > 0 ? "text-gain" : pnl < 0 ? "text-loss" : "text-content-secondary";
    const pfText = pf >= 0 ? pf.toFixed(2) : "—";
    const status = resolveStatus({ openCount, lastTradeTs, trades });
    const hasActive = openCount > 0;
    const lossWarn = !hasActive && pnl <= LOSS_WARN_KRW;
    const uptime = formatUptime(firstTradeTs);

    const borderAccent = hasActive
        ? "border-l-2 border-l-brand"
        : lossWarn
          ? "border-l-2 border-l-loss"
          : "border-l-2 border-l-brand/30";

    const toggle = () => setOpen((v) => !v);

    return (
        <div
            className={`bg-surface-card border border-surface-border rounded-lg ${borderAccent}`}
        >
            <button
                type="button"
                onClick={toggle}
                aria-expanded={open}
                aria-label={`${displayName} 패밀리 (${memberCount} ${
                    variantLabel(group.variantAxis).unit
                }) ${open ? "접기" : "펼치기"}`}
                className="text-left w-full p-3 rounded-lg hover:bg-surface-hover transition min-h-11"
            >
                <div className="flex items-start justify-between mb-1.5 gap-2">
                    <div className="flex items-start gap-1.5 min-w-0">
                        {adoptedCount > 0 && (
                            <span
                                className="text-warning shrink-0 mt-0.5"
                                title={`${adoptedCount}/${memberCount} 코인 채택됨`}
                                aria-hidden="true"
                            >
                                ⭐
                            </span>
                        )}
                        {groupIsNew && <span className="mt-0.5 shrink-0"><NewBadge /></span>}
                        <span className="text-sm font-semibold text-content leading-snug line-clamp-2">
                            {displayName}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <VerdictBadge group={group} verdict={verdict} fields={f} strategy={group} />
                        <StatusBadge status={status} />
                    </div>
                </div>
                <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                    <span
                        className="text-xs text-brand-dark bg-brand-subtle px-1.5 py-0.5 rounded"
                        title={variantLabel(group.variantAxis).title}
                    >
                        {memberCount} {variantLabel(group.variantAxis).unit}
                    </span>
                    {showFamily && family && (
                        <span className="inline-flex items-center gap-1 text-xs text-content-muted bg-surface-elevated px-1.5 py-0.5 rounded">
                            <span aria-hidden="true">{family.glyph}</span>
                            {family.label}
                        </span>
                    )}
                </div>
                {summary && (
                    <p className="text-xs text-content-secondary leading-relaxed line-clamp-2 mb-2">
                        {summary}
                    </p>
                )}
                <CapitalStat pnlKrw={pnl} units={memberCount} />
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                        <div className="text-content-muted">PnL (KRW) · 합산</div>
                        <div className={`text-sm font-semibold ${pnlClass}`}>
                            {pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </div>
                    </div>
                    <div>
                        <div className="text-content-muted">거래 · 합산</div>
                        <div className="text-sm font-semibold text-content">{trades}</div>
                    </div>
                    <div>
                        <div className="text-content-muted">WR</div>
                        <div className="text-sm font-semibold text-content">
                            {(wr * 100).toFixed(1)}%
                        </div>
                    </div>
                    <div>
                        <div className="text-content-muted">PF</div>
                        <div className="text-sm font-semibold text-content">{pfText}</div>
                    </div>
                </div>
                <div className="mt-2 pt-2 border-t border-surface-border-light grid grid-cols-2 gap-2 text-xs text-content-muted">
                    <div className="truncate">
                        운영 <span className="text-content">{uptime}</span>
                    </div>
                    <div className="truncate text-right">
                        {lastTradeTs ? (
                            <>최근 <span className="text-content">{formatKstShort(lastTradeTs)}</span></>
                        ) : (
                            <span>거래 없음</span>
                        )}
                    </div>
                </div>
                {hasActive && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-brand-light font-medium">
                        <span
                            aria-hidden="true"
                            className="inline-block h-1.5 w-1.5 rounded-full bg-brand animate-pulse"
                        />
                        활성 포지션 {openCount}
                    </div>
                )}
                <div className="mt-2 flex items-center gap-1 text-xs text-content-secondary">
                    <Chevron open={open} />
                    {open
                        ? `${variantLabel(group.variantAxis).unit}별 접기`
                        : `${variantLabel(group.variantAxis).unit}별 보기 (${memberCount})`}
                </div>
            </button>

            {open && (
                <div className="px-3 pb-3 -mt-1">
                    <ul className="divide-y divide-surface-border-light border-t border-surface-border-light">
                        {members.map((m) => (
                            <MemberRow
                                key={readStrategyFields(m).id}
                                member={m}
                                adopted={adoptedSet.has(readStrategyFields(m).id)}
                                isNew={newSet.has(readStrategyFields(m).id)}
                                onClick={onMemberClick}
                            />
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function MemberRow({ member, adopted, isNew = false, onClick }) {
    const f = readStrategyFields(member);
    const { id, pnl, trades, openCount, lastTradeTs } = f;
    const status = resolveStatus({ openCount, lastTradeTs, trades });
    const coin = coinLabel(id);
    const pnlClass =
        pnl > 0 ? "text-gain" : pnl < 0 ? "text-loss" : "text-content-secondary";
    return (
        <li>
            <button
                type="button"
                onClick={() => onClick && onClick(id)}
                aria-label={`${id} 디테일 보기 (상태: ${status.label}${isNew ? ", 신규" : ""})`}
                className="w-full flex items-center justify-between gap-2 py-2 text-left hover:bg-surface-hover rounded px-1 min-h-11 transition"
            >
                <span className="flex items-center gap-2 min-w-0">
                    <span
                        aria-hidden="true"
                        className={`inline-block h-1.5 w-1.5 rounded-full ${dotClassOf(status.tone)}`}
                    />
                    {adopted && (
                        <span className="text-warning shrink-0" aria-hidden="true">
                            ⭐
                        </span>
                    )}
                    {isNew && (
                        <span
                            className="text-brand-dark shrink-0"
                            title="최근 추가된 코인"
                            aria-hidden="true"
                        >
                            ✦
                        </span>
                    )}
                    <span className="text-xs font-semibold text-content truncate">
                        {coin}
                    </span>
                    <span className="text-xs text-content-muted truncate">{id}</span>
                </span>
                <span className="flex items-center gap-3 shrink-0 text-xs">
                    <span className={`font-semibold tabular-nums ${pnlClass}`}>
                        {pnl.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="text-content-muted tabular-nums">{trades}건</span>
                    <span className="text-content-muted" aria-hidden="true">
                        ›
                    </span>
                </span>
            </button>
        </li>
    );
}

function Chevron({ open }) {
    return (
        <svg
            aria-hidden="true"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${open ? "rotate-180" : ""}`}
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}

function StatusBadge({ status }) {
    return (
        <span
            className="inline-flex items-center gap-1 text-xs text-content-secondary shrink-0"
            title={status.title}
        >
            <span
                aria-hidden="true"
                className={`inline-block h-1.5 w-1.5 rounded-full ${dotClassOf(status.tone)}`}
            />
            {status.label}
        </span>
    );
}

function dotClassOf(tone) {
    return (
        {
            brand: "bg-brand animate-pulse",
            gain: "bg-gain",
            warning: "bg-warning",
            muted: "bg-content-muted",
        }[tone] ?? "bg-content-muted"
    );
}

/**
 * 그룹 합산 상태 — classifyStrategy 와 동일 규칙이되 카드 표시용 label/tone/title 부여.
 * (PaperStrategyCard 의 resolveStatus 와 동일 로직 — 그룹 집계값에 적용)
 */
function resolveStatus({ openCount, lastTradeTs, trades }) {
    const bucket = classifyStrategy({ openCount, lastTradeTs, trades });
    switch (bucket) {
        case "active":
            return { label: "진입중", tone: "brand", title: "활성 포지션 보유" };
        case "waiting":
            return {
                label: "신호 대기",
                tone: "muted",
                title: "진입 신호 대기 — 조건 충족 시 자동 진입",
            };
        case "idle": {
            const h = lastTradeTs
                ? Math.floor((Date.now() - new Date(lastTradeTs).getTime()) / 3600 / 1000)
                : 0;
            return { label: "휴면", tone: "warning", title: `${h}시간 동안 거래 없음` };
        }
        case "live":
        default: {
            const h = lastTradeTs
                ? (Date.now() - new Date(lastTradeTs).getTime()) / 3600 / 1000
                : null;
            return {
                label: "활성",
                tone: "gain",
                title: h != null ? `${h.toFixed(1)}시간 전 거래` : "최근 거래 시각 미상",
            };
        }
    }
}

/**
 * strategy_id 끝의 코인 토큰을 대문자 라벨로. 코인 접미사가 없으면 id 그대로.
 * (strategyGrouping.parseStrategyId 와 동일 규칙이지만 표시용 라벨만 필요해 경량 처리)
 */
function coinLabel(id) {
    const idx = typeof id === "string" ? id.lastIndexOf("_") : -1;
    if (idx <= 0 || idx === id.length - 1) return id;
    return id.slice(idx + 1).toUpperCase();
}

function formatUptime(firstTs) {
    if (!firstTs) return "—";
    const t = new Date(firstTs).getTime();
    if (isNaN(t)) return "—";
    const ms = Math.max(0, Date.now() - t);
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (days > 0) return `${days}일 ${hours}시간`;
    if (hours > 0) return `${hours}시간 ${minutes}분`;
    return `${minutes}분`;
}
